import React, { useState, useRef, useEffect, useCallback } from "react";
import ReactDOM from "react-dom";
import { useSelector, useDispatch } from "react-redux";
import { getAccountBalance, payForResumeAnalysis, checkResumeAnalysisPayment, requestProfessionalResumeReview } from "../../../redux/actions/walletActions";
import { numberWithCommas } from "../../../utils/helpers";
import "./ResumeAnalysisModal.css";

const ANALYSIS_COST_USD = 29.00;
const ANALYSIS_COST_NGN = 7999.00;

// ─── HTML Entity / special-char decoder ──────────────────────────────────────
// Handles two distinct corruption patterns the API produces:
//
// Pattern A – interleaved ampersands:  "&m&i&c&r&o&s&e&r&v&i&c&e&s"
//   The API inserts "&" before EVERY character — including "&", "'", "-", spaces.
//   e.g. "&R&e&p&l&a&c&e& &n&o&n&-&s&t&a&n&d&a&r&d&"
//   or   "&I&n&t&e&g&r&a&t&i&n&g& &A&I" or "&&'&&" (apostrophe corruption)
//
//   Detection: count "bare &" (& NOT starting a valid HTML entity) vs non-& chars.
//   Corrupted strings score ≥ 0.6 (nearly one bare-& per real char).
//   Clean prose with a few ampersands scores < 0.05.
//
//   Fix: strip every bare "&" leaving only real entities (&amp; &#39; etc.) intact.
//
// Pattern B – standard HTML entities:  "&amp;" "&lt;" "&#39;" etc.
//   Fix: decode via a hidden <textarea> (browser handles all cases).

const BARE_AMP_RE = /&(?![a-zA-Z]{1,8};|#\d{1,6};|#x[0-9a-fA-F]{1,6};)/g;

const decodeHtml = (raw) => {
  if (!raw || typeof raw !== "string") return raw ?? "";

  let str = raw;

  // ── Step 1: decode HTML entities FIRST ───────────────────────────────────
  // The API double-encodes: the "&" in "&R&e&p" is itself stored as "&amp;"
  // so the raw JS string is "&amp;R&amp;e&amp;p" not "&R&e&p".
  // We must decode entities before we can detect the interleaving pattern.
  try {
    const ta = document.createElement("textarea");
    ta.innerHTML = str;
    str = ta.value;
  } catch (_) {
    str = str
      .replace(/&amp;/g, "&")
      .replace(/&lt;/g, "<")
      .replace(/&gt;/g, ">")
      .replace(/&quot;/g, '"')
      .replace(/&#39;/g, "'")
      .replace(/&apos;/g, "'")
      .replace(/&nbsp;/g, " ");
  }

  // ── Step 2: strip interleaved-& corruption (&R&e&p&l&a&c&e → Replace) ────
  // After entity decode, corrupted strings look like "&R&e&p&l&a&c&e".
  // Detection: ratio of bare "&" to non-& chars >= 0.6.
  // Clean prose with occasional & scores ~0.01-0.05, never reaches 0.6.
  const bareAmps  = (str.match(BARE_AMP_RE) || []).length;
  const nonAmpLen = str.replace(BARE_AMP_RE, "").length;
  if (nonAmpLen > 0 && bareAmps / nonAmpLen >= 0.6) {
    str = str.replace(BARE_AMP_RE, "");
  }

  return str;
};

// Decode every string inside an object/array recursively
const deepDecode = (value) => {
  if (typeof value === "string") return decodeHtml(value);
  if (Array.isArray(value)) return value.map(deepDecode);
  if (value && typeof value === "object") {
    const out = {};
    for (const k of Object.keys(value)) out[k] = deepDecode(value[k]);
    return out;
  }
  return value;
};

// Alias used both in JSX and inside handleDownload
const safe = (val) => decodeHtml(String(val ?? ""));

// PDF-only: replace Unicode punctuation jsPDF helvetica cannot encode (latin-1 only)
const cleanForPdf = (val) => {
  let s = String(val ?? "");
  s = s.replace(/[\u2018\u2019\u02BC\u2032]/g, "'");
  s = s.replace(/[\u201C\u201D\u2033]/g, '"');
  s = s.replace(/[\u2013\u2014\u2011\u2012\u2010]/g, '-');
  s = s.replace(/\u2026/g, '...');
  s = s.replace(/[\u2022\u25CF\u25E6\u2023]/g, '*');
  s = s.replace(/[^\u0020-\u007E\u00A0-\u00FF]/g, '');
  return s;
};
// ─────────────────────────────────────────────────────────────────────────────

const ResumeAnalysisModal = ({ isOpen, onClose, analysisData, requiresPayment = true }) => {
  const dispatch = useDispatch();
  const [hasPaid, setHasPaid] = useState(false);
  const [showPaywall, setShowPaywall] = useState(false);
  const [paymentLoading, setPaymentLoading] = useState(false);
  const [checkingPayment, setCheckingPayment] = useState(false);
  const [requestingReview, setRequestingReview] = useState(false);
  const [reviewRequested, setReviewRequested] = useState(false);
  const contentRef = useRef(null);
  const blurredRef = useRef(null);
  const paywallTriggered = useRef(false);
  const { balance } = useSelector((state) => state.wallet);
  const { userData } = useSelector((state) => state.auth);

  const [modalToast, setModalToast] = useState({ show: false, message: "", type: "info" });

  const showModalToast = (type = "info", message, duration = 4000) => {
    setModalToast({ show: true, message, type });
    setTimeout(() => setModalToast({ show: false, message: "", type: "info" }), duration);
  };

  const isNigerian = userData?.country === "160";
  const analysisCost = isNigerian ? ANALYSIS_COST_NGN : ANALYSIS_COST_USD;
  const currencySymbol = isNigerian ? "\u20A6" : "$";
  const currencyLabel = isNigerian ? "NGN" : "USD";

  useEffect(() => {
    if (isOpen) {
      if (requiresPayment) {
        getAccountBalance(dispatch);
        setCheckingPayment(true);
        checkResumeAnalysisPayment()
          .then((res) => { if (res.has_paid) { setHasPaid(true); setShowPaywall(false); } })
          .finally(() => setCheckingPayment(false));
      } else {
        setHasPaid(true);
        setShowPaywall(false);
      }
    }
  }, [isOpen, dispatch, requiresPayment]);

  const getCurrentBalance = () => (!balance || !balance[0]) ? 0 : parseFloat(balance[0].available);
  const getBookBalance    = () => (!balance || !balance[0]) ? 0 : parseFloat(balance[0].balance || 0);
  const getTotalBalance   = () => getCurrentBalance() + getBookBalance();

  const handleScroll = useCallback(() => {
    if (!requiresPayment || hasPaid || paywallTriggered.current || !contentRef.current || !blurredRef.current) return;
    const containerRect = contentRef.current.getBoundingClientRect();
    const blurredRect   = blurredRef.current.getBoundingClientRect();
    if (blurredRect.top < containerRect.bottom - 60) {
      paywallTriggered.current = true;
      setShowPaywall(true);
    }
  }, [hasPaid, requiresPayment]);

  useEffect(() => {
    const el = contentRef.current;
    if (!el) return;
    el.addEventListener("scroll", handleScroll);
    return () => el.removeEventListener("scroll", handleScroll);
  }, [handleScroll, isOpen]);

  useEffect(() => {
    if (isOpen) { paywallTriggered.current = false; setShowPaywall(false); }
  }, [isOpen]);

  if (!isOpen || !analysisData) return null;

  const data = deepDecode(analysisData);
  const { job_family, job_level, overall_score, sectional_grades, top_fixes,
          rewrite_suggestions, semantic_recommendations, ats_findings } = data;

  const getScoreColor = (score) => score >= 80 ? "#10b981" : score >= 60 ? "#f59e0b" : "#ef4444";

  const handleRequestProfessionalReview = async () => {
    if (reviewRequested) {
      showModalToast("info", "You've already requested a professional review. Our team will contact you soon.");
      return;
    }
    setRequestingReview(true);
    try {
      const result = await requestProfessionalResumeReview({
        resume_score: overall_score,
        resume_id: analysisData?.resume_id || userData?.resume || null,
        resume_link: userData?.resume || null,
        analysis_report_link: analysisData?.report_link || null,
      });
      if (result.status) {
        showModalToast("success", "Request sent! Our team will contact you within 24-48 hours with a free professional report.", 5000);
        setReviewRequested(true);
      } else {
        showModalToast("error", result.message || "Failed to send request. Please try again.");
      }
    } catch {
      showModalToast("error", "Failed to send request. Please try again.");
    } finally {
      setRequestingReview(false);
    }
  };

  const handlePayClick = async () => {
    const totalBalance = getTotalBalance();
    if (totalBalance < analysisCost) {
      showModalToast("warn",
        `Insufficient balance (${currencySymbol}${numberWithCommas(totalBalance.toFixed(2))}). You need ${currencySymbol}${numberWithCommas(analysisCost.toFixed(2))}.`,
        4000
      );
      setTimeout(() => { history.push('/wallet'); }, 4000); // eslint-disable-line no-restricted-globals
      return;
    }
    setPaymentLoading(true);
    try {
      const result = await payForResumeAnalysis(dispatch, {
        analysis_type: "resume_analysis",
        resume_id: analysisData?.resume_id || userData?.resume || null,
      });
      if (result.type === "PAY_FOR_RESUME_ANALYSIS") {
        showModalToast("success", "Payment successful! Full report unlocked.");
        setHasPaid(true);
        setShowPaywall(false);
        getAccountBalance(dispatch);
        checkResumeAnalysisPayment().then((res) => { if (res.has_paid) setHasPaid(true); });
      } else {
        showModalToast("error", result.payload || "Payment failed. Please try again.");
      }
    } catch {
      showModalToast("error", "Payment failed. Please try again.");
    } finally {
      setPaymentLoading(false);
    }
  };

  // ── PDF Download ────────────────────────────────────────────────────────────
  const handleDownload = () => {
    if (!hasPaid) { setShowPaywall(true); return; }
    const { jsPDF } = window.jspdf;
    if (!jsPDF) { showModalToast("error", "PDF library not loaded. Please refresh and try again."); return; }

    const doc = new jsPDF("p", "mm", "a4");
    const PW  = doc.internal.pageSize.getWidth();   // 210
    const PH  = doc.internal.pageSize.getHeight();  // 297
    const M   = 12;   // page margin
    const CW  = PW - M * 2;  // 186
    const LH  = 5;
    let y     = 0;

    // ── helpers ──────────────────────────────────────────────────────────────
    // data is already deepDecode()'d — cleanForPdf() handles jsPDF latin-1 conversion
    const txt  = (text, maxW, fs) => { doc.setFontSize(fs); return doc.splitTextToSize(cleanForPdf(text), maxW); };
    const pb   = (need) => { if (y + need > PH - M) { doc.addPage(); y = M + 4; } };
    const gap  = (n = 4) => { y += n; };

    // Draw a pill badge (text centred inside rounded rect)
    const badge = (label, bx, by, bw, bh, bg, fg, fs) => {
      doc.setFillColor(...bg); doc.roundedRect(bx, by, bw, bh, bh / 2, bh / 2, "F");
      doc.setFontSize(fs); doc.setFont("helvetica", "bold"); doc.setTextColor(...fg);
      doc.text(label, bx + bw / 2, by + bh / 2 + fs * 0.18, { align: "center" });
    };

    // Draw a section card header; returns startY. Advances y by 13.
    const cardHeader = (title, icon = "") => {
      doc.setFillColor(58, 110, 212);
      doc.roundedRect(M, y, 3, 8, 1.5, 1.5, "F");
      doc.setFontSize(10); doc.setFont("helvetica", "bold"); doc.setTextColor(30, 30, 30);
      doc.text((icon ? icon + "  " : "") + title, M + 6, y + 6);
      y += 11;
      doc.setDrawColor(230, 232, 240); doc.setLineWidth(0.25);
      doc.line(M, y, M + CW, y);
      y += 5;
    };

    const bulletLine = (text, maxW, fs = 8.5, dotColor = [58, 110, 212]) => {
      const lines = txt(text, maxW - 6, fs);
      pb(lines.length * LH + 3);
      doc.setFillColor(...dotColor);
      doc.circle(M + 3.5, y + 1.5, 1.1, "F");
      doc.setFontSize(fs); doc.setFont("helvetica", "normal"); doc.setTextColor(55, 55, 55);
      doc.text(lines, M + 7, y);
      y += lines.length * LH + 2;
    };

    const dateStr = new Date().toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" });

    // ── HEADER BAR ───────────────────────────────────────────────────────────
    doc.setFillColor(30, 58, 138);
    doc.rect(0, 0, PW, 18, "F");
    // thin accent stripe
    doc.setFillColor(99, 155, 255);
    doc.rect(0, 18, PW, 1.5, "F");
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(13); doc.setFont("helvetica", "bold");
    doc.text("Resume Analysis Report", M, 11.5);
    doc.setFontSize(8); doc.setFont("helvetica", "normal");
    doc.text(dateStr, PW - M, 11.5, { align: "right" });
    y = 26;

    // ── SCORE CARD ───────────────────────────────────────────────────────────
    const SCH = 38;
    // card bg
    doc.setFillColor(42, 58, 90);
    doc.roundedRect(M, y, CW, SCH, 4, 4, "F");

    // Score circle
    const cx = M + 22, cy = y + SCH / 2, cr = 14;
    const scoreCol = overall_score >= 80 ? [16, 185, 129] : overall_score >= 60 ? [245, 158, 11] : [239, 68, 68];
    doc.setDrawColor(255, 255, 255, 0.15); doc.setLineWidth(3);
    doc.circle(cx, cy, cr, "S");
    doc.setDrawColor(...scoreCol); doc.setLineWidth(3);
    doc.circle(cx, cy, cr, "S"); // simplified — jsPDF can't do arc, so full ring in score colour
    doc.setFillColor(28, 40, 65); doc.circle(cx, cy, cr - 2, "F");
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(15); doc.setFont("helvetica", "bold");
    doc.text(String(overall_score), cx, cy + 1.5, { align: "center" });
    doc.setFontSize(6.5); doc.setFont("helvetica", "normal");
    doc.text("/100", cx, cy + 7, { align: "center" });

    // Score label under circle
    doc.setFontSize(6.5); doc.setTextColor(...scoreCol);
    const scoreLabel = overall_score >= 80 ? "Excellent" : overall_score >= 60 ? "Good" : "Needs Work";
    doc.text(scoreLabel, cx, cy + 13, { align: "center" });

    // Meta fields (Job Family + Job Level)
    const mx = M + 44;
    const fieldW = (CW - 44 - 8) / 2;
    [[job_family || "N/A", "JOB FAMILY"], [job_level || "N/A", "JOB LEVEL"]].forEach(([val, label], i) => {
      const fx = mx + i * (fieldW + 4);
      doc.setTextColor(160, 180, 215); doc.setFontSize(6.5); doc.setFont("helvetica", "normal");
      doc.text(label, fx, y + 9);
      doc.setTextColor(255, 255, 255); doc.setFontSize(10); doc.setFont("helvetica", "bold");
      const lines = txt(val, fieldW - 2, 10);
      doc.text(lines, fx, y + 16);
    });
    y += SCH + 8;

    // ── SECTIONAL GRADES ─────────────────────────────────────────────────────
    const grades = Object.entries(sectional_grades || {});
    if (grades.length > 0) {
      pb(grades.length * 10 + 22);
      cardHeader("Sectional Grades");
      // 2-column grid
      const colW2 = (CW - 4) / 2;
      grades.forEach(([key, value], i) => {
        const col = i % 2;
        const row = Math.floor(i / 2);
        const gx  = M + col * (colW2 + 4);
        const gy  = y + row * 11;
        pb(12);
        doc.setFillColor(245, 247, 252);
        doc.roundedRect(gx, gy - 1, colW2, 10, 2, 2, "F");
        doc.setFontSize(7.5); doc.setFont("helvetica", "normal"); doc.setTextColor(70, 70, 70);
        const lbl = safe(String(key)).replace(/_/g, " ").replace(/\b\w/g, c => c.toUpperCase());
        doc.text(lbl, gx + 4, gy + 5.5);
        badge(safe(String(value)), gx + colW2 - 18, gy + 1, 16, 7, [99, 126, 234], [255, 255, 255], 7);
      });
      y += Math.ceil(grades.length / 2) * 11 + 4;
      gap(6);
    }

    // ── TOP FIXES ─────────────────────────────────────────────────────────────
    const fixes = top_fixes || [];
    if (fixes.length > 0) {
      pb(fixes.length * (LH + 2) + 22);
      cardHeader("Top Fixes");
      fixes.forEach(fix => bulletLine(fix, CW));
      gap(6);
    }

    // ── REWRITE SUGGESTIONS ──────────────────────────────────────────────────
    const rewrites = rewrite_suggestions || [];
    if (rewrites.length > 0) {
      pb(30);
      cardHeader("Rewrite Suggestions");

      rewrites.forEach((s, i) => {
        const orig   = safe(s.original || "");
        const rw     = safe(s.rewrite  || "");
        const reason = safe(s.reason   || "");

// Measure heights — tighter column widths to prevent text overflow
const colW    = (CW - 16) / 2;        // subtract more for padding + arrow gap
const textW   = colW - 10;            // inner text width with left+right padding
const oLines  = txt(orig,   textW, 8);
const rLines  = txt(rw,     textW, 8);
const rLines2 = txt(reason, CW - 20, 8);  // reason also needs inner margin
const blockH  = Math.max(oLines.length, rLines.length) * LH + 18;
const reasonH = rLines2.length * LH + 14;
const cardH   = blockH + reasonH + 10;

        pb(Math.min(cardH + 4, PH - 28));
        const cY = y;

        // Card background
        doc.setFillColor(255, 249, 230);
        doc.roundedRect(M, cY, CW, cardH, 3, 3, "F");
        doc.setDrawColor(253, 196, 80); doc.setLineWidth(0.3);
        doc.roundedRect(M, cY, CW, cardH, 3, 3, "S");

        // Number pill
        badge(`#${i + 1}`, M + 4, cY + 4, 14, 6.5, [255, 255, 255], [220, 100, 60], 7);

        const bY = cY + 14;

        // ORIGINAL panel
        doc.setFillColor(255, 255, 255);
        doc.roundedRect(M + 4, bY, colW, blockH - 4, 2, 2, "F");
        doc.setFontSize(6); doc.setFont("helvetica", "bold"); doc.setTextColor(150, 150, 150);
        doc.text("ORIGINAL", M + 7, bY + 5);
        doc.setFontSize(8); doc.setFont("helvetica", "normal"); doc.setTextColor(50, 50, 50);
    doc.text(oLines, M + 8, bY + 11);

        // Arrow
        doc.setFontSize(11); doc.setTextColor(180, 160, 120);
        doc.text("\u2192", M + 4 + colW + 1.5, bY + blockH / 2 - 1);

        // REWRITE panel
const rwX = M + 4 + colW + 12;   // more gap for arrow
const rwW = CW - colW - 16;  
        doc.setFillColor(240, 255, 245);
        doc.roundedRect(rwX, bY, rwW, blockH - 4, 2, 2, "F");
        doc.setFontSize(6); doc.setFont("helvetica", "bold"); doc.setTextColor(16, 150, 80);
        doc.text("REWRITE", rwX + 3, bY + 5);
        doc.setFontSize(8); doc.setFont("helvetica", "normal"); doc.setTextColor(30, 90, 50);
      doc.text(txt(rw, rwW - 8, 8), rwX + 4, bY + 11);

        // REASON box
        const rsY = bY + blockH + 1;
        doc.setFillColor(255, 255, 255);
        doc.roundedRect(M + 4, rsY, CW - 8, reasonH, 2, 2, "F");
        doc.setFillColor(220, 100, 60);
        doc.roundedRect(M + 4, rsY, 2.5, reasonH, 1, 1, "F");
        doc.setFontSize(7.5); doc.setFont("helvetica", "bold"); doc.setTextColor(220, 100, 60);
        doc.text("Reason:", M + 9, rsY + 5.5);
        doc.setFont("helvetica", "normal"); doc.setTextColor(60, 60, 60);
       doc.text(rLines2, M + 30, rsY + 5.5);

y = cY + cardH + 10;   // more breathing room between cards
      });
      gap(4);
    }

    // ── SEMANTIC RECOMMENDATIONS ──────────────────────────────────────────────
    const semItems = semantic_recommendations || [];
    if (semItems.length > 0) {
      pb(semItems.length * (LH + 2) + 22);
      cardHeader("Semantic Recommendations");
      semItems.forEach(rec => bulletLine(rec, CW, 8.5, [16, 185, 129]));
      gap(6);
    }

    // ── ATS COMPATIBILITY ─────────────────────────────────────────────────────
    if (ats_findings) {
      const atsRecs = ats_findings.recommendations || [];
      pb(60);
      cardHeader("ATS Compatibility");

      const checks = [
        { label: "Multi-Column Layout",     issue: ats_findings.has_multi_column_layout,          yes: "Detected",    no: "Not Detected" },
        { label: "Contact Info Placement",  issue: ats_findings.contact_info_placement_issue,     yes: "Issue Found", no: "OK" },
        { label: "Non-Standard Characters", issue: ats_findings.non_standard_characters_detected, yes: "Detected",    no: "Not Detected" },
      ];

      checks.forEach(c => {
        pb(13);
        const bg = c.issue ? [255, 243, 220] : [220, 252, 231];
        const fg = c.issue ? [180, 90, 10]   : [5, 120, 60];
        doc.setFillColor(...bg);
        doc.roundedRect(M, y - 2, CW, 11, 2, 2, "F");
        // status dot
        doc.setFillColor(...fg); doc.circle(M + 5, y + 3.5, 2.5, "F");
        doc.setFontSize(8.5); doc.setFont("helvetica", "normal"); doc.setTextColor(...fg);
        doc.text(`${c.label}: ${c.issue ? c.yes : c.no}`, M + 11, y + 5.5);
        y += 12;
      });
      gap(4);

      if (atsRecs.length > 0) {
        pb(atsRecs.length * (LH + 2) + 16);
        doc.setFillColor(235, 238, 252);
        const recBlockH = atsRecs.length * (LH + 3) + 12;
        doc.roundedRect(M, y, CW, recBlockH, 2, 2, "F");
        doc.setFillColor(99, 126, 234);
        doc.roundedRect(M, y, 3, recBlockH, 1, 1, "F");
        doc.setFontSize(8.5); doc.setFont("helvetica", "bold"); doc.setTextColor(80, 100, 200);
        doc.text("ATS Recommendations:", M + 6, y + 7);
        y += 11;
        atsRecs.forEach(rec => {
          const lines = txt(rec, CW - 14, 8);
          pb(lines.length * LH + 3);
          doc.setFontSize(8); doc.setFont("helvetica", "normal");
          doc.setTextColor(99, 126, 234); doc.text("\u2192", M + 5, y);
          doc.setTextColor(55, 55, 55); doc.text(lines, M + 11, y);
          y += lines.length * LH + 2;
        });
        y += 4;
      }
    }

    // ── FOOTER ───────────────────────────────────────────────────────────────
    // Print on last page only
    doc.setDrawColor(200, 205, 220); doc.setLineWidth(0.25);
    doc.line(M, y + 4, M + CW, y + 4);
    doc.setFontSize(7.5); doc.setTextColor(160, 165, 175); doc.setFont("helvetica", "normal");
    doc.text(`Generated by workbrook  |  ${dateStr}`, PW / 2, y + 9, { align: "center" });

    doc.save(`resume-analysis-report-${Date.now()}.pdf`);
    showModalToast("success", "PDF downloaded successfully!");
  };
  // ── End PDF ───────────────────────────────────────────────────────────────

  return ReactDOM.createPortal(
    <>
      <div className="resume-analysis-overlay" onClick={onClose}></div>
      <div className="resume-analysis-modal">
        <div className="modal-header">
          <h2>Resume Analysis Report</h2>
          <button className="close-btn" onClick={onClose}>×</button>
        </div>

        <div className="modal-content" ref={contentRef}>
          {/* Score Card */}
          <div className="score-card">
            <div className="score-circle-container">
              <svg className="score-circle" viewBox="0 0 120 120">
                <circle className="score-circle-bg" cx="60" cy="60" r="54"></circle>
                <circle
                  className="score-circle-progress"
                  cx="60" cy="60" r="54"
                  style={{
                    strokeDashoffset: 339.292 - (339.292 * overall_score) / 100,
                    stroke: getScoreColor(overall_score),
                  }}
                ></circle>
              </svg>
              <div className="score-text">
                <span className="score-value">{overall_score}</span>
                <span className="score-max">/100</span>
              </div>
            </div>
            <div className="score-details">
              <div className="detail-item">
                <span className="detail-label">Job Family</span>
                <span className="detail-value">{job_family}</span>
              </div>
              <div className="detail-item">
                <span className="detail-label">Job Level</span>
                <span className="detail-value">{job_level}</span>
              </div>
            </div>
          </div>

          {/* Professional Review CTA */}
          {hasPaid && (
            <div className="professional-review-cta">
              <div className="cta-content">
                <div className="cta-icon">👨‍💼</div>
                <div className="cta-text">
                  <h4>Want a Free Professional Review?</h4>
                  <p>Get expert feedback and a detailed professional report from our recruitment specialists.</p>
                </div>
              </div>
              <button
                className={`cta-button ${reviewRequested ? "requested" : ""}`}
                onClick={handleRequestProfessionalReview}
                disabled={requestingReview || reviewRequested}
              >
                {requestingReview ? "Sending..." : reviewRequested ? "✓ Request Sent" : "Request Professional Review"}
              </button>
            </div>
          )}

          {/* Locked / unlocked content */}
          <div
            ref={blurredRef}
            className={requiresPayment && !hasPaid ? "locked-content" : ""}
            style={{ position: "relative" }}
          >
            {/* Sectional Grades */}
            <div className="section">
              <h3 className="section-title">Sectional Grades</h3>
              <div className="grades-grid">
                {Object.entries(sectional_grades || {}).map(([key, value]) => (
                  <div key={key} className="grade-item">
                    <div className="grade-label">{safe(key).replace(/_/g, " ").toUpperCase()}</div>
                    <div className="grade-badge">{safe(value)}</div>
                  </div>
                ))}
              </div>
            </div>

            {/* Top Fixes */}
            {top_fixes && top_fixes.length > 0 && (
              <div className="section">
                <h3 className="section-title"><span className="icon">🔧</span> Top Fixes</h3>
                <ul className="fixes-list">
                  {top_fixes.map((fix, idx) => (
                    <li key={idx} className="fix-item">{safe(fix)}</li>
                  ))}
                </ul>
              </div>
            )}

            {/* Rewrite Suggestions */}
            {rewrite_suggestions && rewrite_suggestions.length > 0 && (
              <div className="section">
                <h3 className="section-title"><span className="icon">✍️</span> Rewrite Suggestions</h3>
                <div className="rewrite-suggestions">
                  {rewrite_suggestions.map((suggestion, idx) => (
                    <div key={idx} className="suggestion-card">
                      <div className="suggestion-header">
                        <span className="suggestion-number">#{idx + 1}</span>
                      </div>
                      <div className="suggestion-content">
                        <div className="suggestion-block original">
                          <div className="block-label">Original</div>
                          <div className="block-text">{safe(suggestion.original)}</div>
                        </div>
                        <div className="arrow-icon">→</div>
                        <div className="suggestion-block rewrite">
                          <div className="block-label">Rewrite</div>
                          <div className="block-text">{safe(suggestion.rewrite)}</div>
                        </div>
                      </div>
                      <div className="suggestion-reason">
                        <strong>Reason:</strong> {safe(suggestion.reason)}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Semantic Recommendations */}
            {semantic_recommendations && semantic_recommendations.length > 0 && (
              <div className="section">
                <h3 className="section-title"><span className="icon">💡</span> Semantic Recommendations</h3>
                <ul className="recommendations-list">
                  {semantic_recommendations.map((rec, idx) => (
                    <li key={idx} className="recommendation-item">{safe(rec)}</li>
                  ))}
                </ul>
              </div>
            )}

            {/* ATS Findings */}
            {ats_findings && (
              <div className="section">
                <h3 className="section-title"><span className="icon">🤖</span> ATS Compatibility Check</h3>
                <div className="ats-checks">
                  <div className={`ats-check ${ats_findings.has_multi_column_layout ? "warning" : "success"}`}>
                    <span className="check-icon">{ats_findings.has_multi_column_layout ? "⚠️" : "✓"}</span>
                    <span className="check-text">Multi-Column Layout: {ats_findings.has_multi_column_layout ? "Detected" : "Not Detected"}</span>
                  </div>
                  <div className={`ats-check ${ats_findings.contact_info_placement_issue ? "warning" : "success"}`}>
                    <span className="check-icon">{ats_findings.contact_info_placement_issue ? "⚠️" : "✓"}</span>
                    <span className="check-text">Contact Info Placement: {ats_findings.contact_info_placement_issue ? "Issue Found" : "OK"}</span>
                  </div>
                  <div className={`ats-check ${ats_findings.non_standard_characters_detected ? "warning" : "success"}`}>
                    <span className="check-icon">{ats_findings.non_standard_characters_detected ? "⚠️" : "✓"}</span>
                    <span className="check-text">Non-Standard Characters: {ats_findings.non_standard_characters_detected ? "Detected" : "Not Detected"}</span>
                  </div>
                </div>
                {ats_findings.recommendations && ats_findings.recommendations.length > 0 && (
                  <div className="ats-recommendations">
                    <h4 className="ats-rec-title">ATS Recommendations:</h4>
                    <ul className="ats-rec-list">
                      {ats_findings.recommendations.map((rec, idx) => (
                        <li key={idx} className="ats-rec-item">{safe(rec)}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Paywall */}
        {requiresPayment && showPaywall && !hasPaid && (
          <div className="paywall-overlay" onClick={() => setShowPaywall(false)}>
            <div className="paywall-modal" onClick={(e) => e.stopPropagation()}>
              <div className="paywall-icon">🔒</div>
              <h3 className="paywall-title">Unlock Full Report</h3>
              <p className="paywall-description">
                Get detailed fixes, rewrite suggestions, semantic recommendations,
                and ATS compatibility insights to improve your resume.
              </p>
              <ul className="paywall-features">
                <li>🔧 Top Fixes &amp; Action Items</li>
                <li>✍️ AI-Powered Rewrite Suggestions</li>
                <li>💡 Semantic Recommendations</li>
                <li>🤖 Full ATS Compatibility Report</li>
                <li>⬇️ Downloadable PDF Report</li>
              </ul>
              <div className="paywall-balance">
                <p>Book balance: <strong>{currencySymbol}{numberWithCommas(getBookBalance().toFixed(2))}</strong></p>
                <p>Available balance: <strong>{currencySymbol}{numberWithCommas(getCurrentBalance().toFixed(2))}</strong></p>
                <p>Total balance: <strong>{currencySymbol}{numberWithCommas(getTotalBalance().toFixed(2))}</strong></p>
                <p>Cost: <strong>{currencySymbol}{numberWithCommas(analysisCost.toFixed(2))} {currencyLabel}</strong></p>
              </div>
              <button className="paywall-pay-btn" onClick={handlePayClick} disabled={paymentLoading}>
                {paymentLoading
                  ? "Processing..."
                  : getTotalBalance() >= analysisCost
                  ? `Pay ${currencySymbol}${numberWithCommas(analysisCost.toFixed(2))} from Wallet`
                  : "Top Up Wallet"}
              </button>
              <button className="paywall-dismiss-btn" onClick={() => setShowPaywall(false)}>Maybe Later</button>
            </div>
          </div>
        )}

        <div className="modal-footer">
          {!requiresPayment || hasPaid ? (
            <>
              <button className="download-btn" onClick={handleDownload}>
                <span className="btn-icon">⬇</span>Download Report
              </button>
              <button
                className={`professional-review-footer-btn ${reviewRequested ? "requested" : ""}`}
                onClick={handleRequestProfessionalReview}
                disabled={requestingReview || reviewRequested}
              >
                <span className="btn-icon">👨‍💼</span>
                {requestingReview ? "Sending..." : reviewRequested ? "✓ Review Requested" : "Request Professional Review"}
              </button>
              <button className="close-footer-btn" onClick={onClose}>Close</button>
            </>
          ) : (
            <>
              <button className="pay-footer-btn" onClick={() => setShowPaywall(true)} disabled={paymentLoading}>
                <span className="btn-icon">🔓</span>
                Pay {currencySymbol}{numberWithCommas(analysisCost.toFixed(2))} to Download &amp; View Full Report
              </button>
              <button className="close-footer-btn" onClick={onClose}>Close</button>
            </>
          )}
        </div>
      </div>

      {/* Toast */}
      {modalToast.show && (
        <div className={`modal-toast modal-toast-${modalToast.type}`}>
          <div className="modal-toast-content">
            <span className="modal-toast-icon">
              {modalToast.type === "success" ? "✓" : modalToast.type === "error" ? "✕" : modalToast.type === "warn" ? "⚠" : "ℹ"}
            </span>
            <span className="modal-toast-message">{modalToast.message}</span>
            <button className="modal-toast-close" onClick={() => setModalToast({ show: false, message: "", type: "info" })}>×</button>
          </div>
        </div>
      )}
    </>,
    document.body
  );
};

export default ResumeAnalysisModal;